import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { AdminUser } from '../../models/admin-user';
import { AdminUsersService } from '../../services/admin-users/admin-users';

@Component({
  selector: 'app-admin-users',
  standalone: false,
  templateUrl: './admin-users.html',
  styleUrls: ['./admin-users.css']
})
export class AdminUsers implements OnInit {
  users: AdminUser[] = [];
  loading = false;

  selectedUser: AdminUser | null = null;
  roleModel = {
    role: 2
  };

  updatingRole = false;

  createModel = {
    fullName: '',
    email: '',
    password: '',
    role: 2
  };

  creating = false;

  resetPasswordModel = {
    newPassword: '',
    confirmPassword: ''
  };

  resettingPassword = false;
  lastResetPassword = '';

  constructor(
    private adminUsersService: AdminUsersService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.loading = true;

    this.adminUsersService.getAll().pipe(
      finalize(() => {
        this.loading = false;
        this.cdr.detectChanges();
      })
    ).subscribe({
      next: response => {
        if (!response.success || !Array.isArray(response.data)) {
          this.users = [];
          this.toastr.error(response.message || 'Unable to load admin users.');
          this.cdr.detectChanges();
          return;
        }

        this.users = response.data;
        this.cdr.detectChanges();
      },
      error: error => {
        this.users = [];
        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to load admin users.',
          'Admin Users'
        );
        this.cdr.detectChanges();
      }
    });
  }

  openResetPasswordModal(user: AdminUser): void {
    this.selectedUser = user;
    this.lastResetPassword = '';

    this.resetPasswordModel = {
      newPassword: this.generateTemporaryPassword(),
      confirmPassword: ''
    };

    const modalEl = document.getElementById('resetAdminPasswordModal');
    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  resetPassword(): void {
    if (!this.selectedUser) return;

    if (!this.resetPasswordModel.newPassword.trim()) {
      this.toastr.warning('New password is required.', 'Validation');
      return;
    }

    if (this.resetPasswordModel.newPassword !== this.resetPasswordModel.confirmPassword) {
      this.toastr.warning('Passwords do not match.', 'Validation');
      return;
    }

    this.resettingPassword = true;

    this.adminUsersService
      .resetPassword(this.selectedUser.id, this.resetPasswordModel.newPassword)
      .subscribe({
        next: response => {
          this.resettingPassword = false;

          if (!response.success) {
            this.toastr.error(response.message || 'Unable to reset password.');
            return;
          }

          this.lastResetPassword = this.resetPasswordModel.newPassword;
          this.toastr.success('Password reset successfully.');

          this.loadUsers();
        },
        error: error => {
          this.resettingPassword = false;

          this.toastr.error(
            error?.error?.message ||
            error?.error?.error?.details ||
            'Unable to reset password.',
            'Admin Users'
          );
        }
      });
  }

  copyResetPassword(): void {
    if (!this.lastResetPassword) return;

    navigator.clipboard.writeText(this.lastResetPassword);
    this.toastr.success('Temporary password copied.');
  }

  closeResetPasswordModal(): void {
    const modalEl = document.getElementById('resetAdminPasswordModal');
    const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    modal?.hide();

    this.lastResetPassword = '';
  }

  generateTemporaryPassword(): string {
    const random = Math.random().toString(36).slice(-6);
    return `RiskGuard@${random}1`;
  }

  createAdmin(): void {
    if (!this.createModel.fullName.trim()) {
      this.toastr.warning('Full name is required.', 'Validation');
      return;
    }

    if (!this.createModel.email.trim()) {
      this.toastr.warning('Email is required.', 'Validation');
      return;
    }

    if (!this.createModel.password.trim()) {
      this.toastr.warning('Password is required.', 'Validation');
      return;
    }

    this.creating = true;

    this.adminUsersService.createAdmin({
      fullName: this.createModel.fullName.trim(),
      email: this.createModel.email.trim(),
      password: this.createModel.password,
      role: this.createModel.role
    }).subscribe({
      next: response => {
        this.creating = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to create admin user.');
          return;
        }

        this.toastr.success('Admin user created successfully.');

        this.createModel = {
          fullName: '',
          email: '',
          password: '',
          role: 2
        };

        const modalEl = document.getElementById('createAdminUserModal');
        const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
        modal?.hide();

        this.loadUsers();
      },
      error: error => {
        this.creating = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to create admin user.',
          'Admin Users'
        );
      }
    });
  }

  openRoleModal(user: AdminUser): void {
    this.selectedUser = user;
    this.roleModel = {
      role: user.role
    };

    const modalEl = document.getElementById('changeAdminRoleModal');
    const modal = new (window as any).bootstrap.Modal(modalEl);
    modal.show();
  }

  changeRole(): void {
    if (!this.selectedUser) return;

    this.updatingRole = true;

    this.adminUsersService.changeRole(this.selectedUser.id, {
      role: Number(this.roleModel.role)
    }).subscribe({
      next: response => {
        this.updatingRole = false;

        if (!response.success) {
          this.toastr.error(response.message || 'Unable to change admin role.');
          return;
        }

        this.toastr.success('Admin role updated successfully.');

        const modalEl = document.getElementById('changeAdminRoleModal');
        const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
        modal?.hide();

        this.loadUsers();
      },
      error: error => {
        this.updatingRole = false;

        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to change admin role.',
          'Admin Users'
        );
      }
    });
  }

  changeStatus(user: AdminUser, action: 'activate' | 'disable'): void {
    const confirmed = confirm(`Are you sure you want to ${action} ${user.email}?`);

    if (!confirmed) return;

    const request =
      action === 'activate'
        ? this.adminUsersService.activate(user.id)
        : this.adminUsersService.disable(user.id);

    request.subscribe({
      next: response => {
        if (!response.success) {
          this.toastr.error(response.message || 'Unable to update admin user.');
          return;
        }

        this.toastr.success(`Admin user ${action}d successfully.`);
        this.loadUsers();
      },
      error: error => {
        this.toastr.error(
          error?.error?.message ||
          error?.error?.error?.details ||
          'Unable to update admin user.',
          'Admin Users'
        );
      }
    });
  }

  getRoleLabel(role: number): string {
    switch (role) {
      case 1:
        return 'SuperAdmin';
      case 2:
        return 'Admin';
      case 3:
        return 'Support';
      default:
        return 'Unknown';
    }
  }

  getStatusLabel(status: number): string {
    switch (status) {
      case 1:
        return 'Active';
      case 2:
        return 'Disabled';
      case 3:
        return 'Locked';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 1:
        return 'text-bg-success';
      case 2:
        return 'text-bg-secondary';
      case 3:
        return 'text-bg-warning';
      default:
        return 'text-bg-light';
    }
  }
}
